import { merge } from "lodash"
import { TFunction } from "i18next"

import { getStorage } from "@/utils/storage"
import { metricUnitRegex, unitSquadRegex } from "@/constants/regex"
import { SquareUnit } from "@/constants/unit"
import { ActionDataType } from "@/types/setting"
import { splitByEffortPattern } from "./format"
import { toCapitalize } from "./string"

export function convertUnit(unit: string, units?: any, options?: any) {
  const { unitFormat } = merge({}, { unitFormat: "html" }, options || {})
  const userUnits = merge({}, getStorage("user_units"), units || {})

  const formatSquadUnit = (unit: string, unitFormat: string) =>
    (unitFormat === "html" ? unit.replace(unitSquadRegex, "<sup>$2</sup>") : SquareUnit[unit]) ||
    unit
  const isSquadUnit = (unit: string) => unitSquadRegex.test(unit)

  const convertedUnit = unit ? userUnits[unit.replace(metricUnitRegex, "")]?.unit || unit : ""
  const formattedUnit = isSquadUnit(convertedUnit)
    ? formatSquadUnit(convertedUnit, unitFormat)
    : convertedUnit

  return formattedUnit
}

export type ShortActionSettingType = {
  run: ActionDataType
  accel: ActionDataType
  decel: ActionDataType
}

export function getActionSetting(key: string, actionSettings?: ShortActionSettingType) {
  const [type, index] = key.toLowerCase().split("effort")
  if (actionSettings?.[type as keyof ShortActionSettingType]) {
    return actionSettings[type as keyof ShortActionSettingType].global?.[Number(index) - 1]
  }
}

const acrPatternMap: Record<string, string> = {
  R: "run",
  A: "accel",
  D: "decel",
}

function parseAcrPattern(metric: string): {
  isAcr: boolean
  effortKey?: string
  suffix?: string
} {
  const acrMatch = metric.match(/^acr([RAD])(\d)(Action.+)$/)
  if (acrMatch) {
    const [, type, level, suffix] = acrMatch
    const effortKey = `${acrPatternMap[type]}Effort${level}`

    return { isAcr: true, effortKey, suffix }
  }

  return { isAcr: false }
}

export function getMetricTranslateKey(
  metric: string,
  t: TFunction<"translation", undefined>,
  actionSettings?: ShortActionSettingType,
) {
  if (!metric) {
    return ""
  }

  const acrParsed = parseAcrPattern(metric)
  if (acrParsed.isAcr && acrParsed.effortKey && acrParsed.suffix) {
    const actionName = getActionSetting(acrParsed.effortKey, actionSettings)?.actionName || ""
    const suffixKey = acrParsed.suffix.replace("Action", "").toLowerCase()

    return `${actionName} ${t(suffixKey)} (ACR)`.trim()
  }

  if (metric.toLowerCase().includes("effort")) {
    const [before, effort, ...after] = splitByEffortPattern(metric)

    translateRemainingKeys(after, t)

    return [
      before ? t(before) : "",
      getActionSetting(effort || "", actionSettings)?.actionName || "",
      ...(after.length ? translateRemainingKeys(after, t) : [undefined]),
    ]
      .join(" ")
      .trim()
  }

  return t("metric." + metric)
}

function translateRemainingKeys(
  key: (string | undefined)[],
  t: TFunction<"translation", undefined>,
) {
  const lowerCase = key.map((item) => {
    if (item) {
      return item.toLowerCase()
    }
  })
  if (key.length === 2 && lowerCase.includes("custom") && lowerCase.includes("rel")) {
    return [`(${t("customRel")})`]
  }

  if (lowerCase.includes("custom")) {
    const index = lowerCase.indexOf("custom")
    lowerCase[index] = `(Custom)`
  }

  if (lowerCase.includes("rel")) {
    const index = lowerCase.indexOf("rel")
    lowerCase[index] = `(Individual)`
  }

  return lowerCase
    .map((str, idx) => {
      if (idx === 0) {
        return str
      }
      if (str === undefined) {
        return null
      }

      return toCapitalize(str)
    })
    .map((str) => t(str || ""))
}
